'use client';

import React from 'react';
import { motion } from 'framer-motion';
import styles from './TimelineSection.module.scss';

export interface TimelineItem {
  date: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
}

export interface TimelineSectionProps {
  title: string;
  items: TimelineItem[];
  icon?: React.ReactNode;
}

export const TimelineSection: React.FC<TimelineSectionProps> = ({
  title,
  items,
  icon
}) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const
      }
    }
  };

  const lineVariants = {
    hidden: { scaleY: 0 },
    visible: {
      scaleY: 1,
      transition: {
        duration: 1,
        ease: 'easeInOut' as const
      }
    }
  };

  return (
    <motion.section
      className={styles.timelineSection}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={containerVariants}
    >
      <motion.h2 className={styles.title} variants={itemVariants}>
        {icon && <span className={styles.icon}>{icon}</span>}
        {title}
      </motion.h2>

      <div className={styles.timeline}>
        <motion.div 
          className={styles.line}
          variants={lineVariants}
          style={{ originY: 0 }}
        />
        
        {items.map((item, index) => (
          <motion.div
            key={index}
            className={styles.timelineItem}
            variants={itemVariants}
          >
            <motion.div 
              className={styles.dot}
              whileHover={{ scale: 1.2 }}
              transition={{ type: 'spring', stiffness: 300 }}
            />
            
            <motion.div 
              className={styles.content}
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <div className={styles.date}>{item.date}</div>
              <h3 className={styles.itemTitle}>
                {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
                {item.title}
              </h3>
              <p className={styles.description}>{item.description}</p>
            </motion.div>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
};

export default TimelineSection;